import React, { useState, useEffect } from 'react';
import { Card, Button, message, Space, Typography, Tag, Divider } from 'antd';
import { encryptionService } from '../../utils/encryption';
import { encryptedApiService } from '../../services/EncryptedApiService';
import { fetchBackendEncryptionConfig } from '../../config/encryption';

const { Title, Text, Paragraph } = Typography;

/**
 * Component for testing encryption functionality in the UI
 */
const EncryptionTest: React.FC = () => {
  const [encryptionStatus, setEncryptionStatus] = useState<{
    enabled: boolean;
    hasKey: boolean;
  }>({ enabled: false, hasKey: false });

  const [backendConfig, setBackendConfig] = useState<{
    enabled: boolean;
    algorithm: string;
    transformation: string;
    keyLength: number;
  } | null>(null);

  const [testResults, setTestResults] = useState<{
    localTest: { success: boolean; message: string } | null;
    apiTest: { success: boolean; message: string } | null;
  }>({
    localTest: null,
    apiTest: null
  });

  const [loading, setLoading] = useState({
    localTest: false,
    apiTest: false,
    backendConfig: false
  });

  useEffect(() => {
    // Get encryption status
    const status = encryptionService.isEnabled();
    const hasKey = !!process.env.REACT_APP_ENCRYPTION_KEY;
    setEncryptionStatus({ enabled: status, hasKey });

    // Fetch backend configuration
    const fetchConfig = async () => {
      setLoading(prev => ({ ...prev, backendConfig: true }));
      try {
        const config = await fetchBackendEncryptionConfig();
        config.enabled = true;
        setBackendConfig(config);
      } catch (error) {
        console.error('Failed to fetch backend config:', error);
      } finally {
        setLoading(prev => ({ ...prev, backendConfig: false }));
      }
    };

    fetchConfig();
  }, []);

  const handleLocalTest = async () => {
    setLoading(prev => ({ ...prev, localTest: true }));
    
    try {
      const testData = {
        message: 'Hello from UI encryption test!',
        timestamp: Date.now(),
        userId: 123,
        data: { nested: 'object', array: [1, 2, 3] }
      };

      // Test encryption
      const encrypted = encryptionService.encrypt(JSON.stringify(testData));
      console.log('Encrypted data:', encrypted);

      // Test decryption
      const decrypted = encryptionService.decrypt(encrypted);
      const parsedDecrypted = JSON.parse(decrypted);

      const success = JSON.stringify(testData) === JSON.stringify(parsedDecrypted);

      setTestResults(prev => ({
        ...prev,
        localTest: {
          success,
          message: success ? 'Local encryption test passed!' : 'Local encryption test failed!'
        }
      }));

      if (success) {
        message.success('Local encryption test passed!');
      } else {
        message.error('Local encryption test failed!');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setTestResults(prev => ({
        ...prev,
        localTest: {
          success: false,
          message: `Local encryption test failed: ${errorMessage}`
        }
      }));
      message.error(`Local encryption test failed: ${errorMessage}`);
    } finally {
      setLoading(prev => ({ ...prev, localTest: false }));
    }
  };

  const handleApiTest = async () => {
    setLoading(prev => ({ ...prev, apiTest: true }));
    
    try {
      const result = await encryptedApiService.testEncryption();
      
      setTestResults(prev => ({
        ...prev,
        apiTest: {
          success: result.success,
          message: result.message
        }
      }));

      if (result.success) {
        message.success('API encryption test passed!');
      } else {
        message.error('API encryption test failed!');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setTestResults(prev => ({
        ...prev,
        apiTest: {
          success: false,
          message: `API encryption test failed: ${errorMessage}`
        }
      }));
      message.error(`API encryption test failed: ${errorMessage}`);
    } finally {
      setLoading(prev => ({ ...prev, apiTest: false }));
    }
  };

  const handleTestEncryptedEndpoint = async () => {
    try {
      setLoading(prev => ({ ...prev, apiTest: true }));
      
      // Test the encrypted internship endpoint
      const response = await encryptedApiService.post('/encrypted-internships/list', {
        test: true,
        message: 'Testing encrypted endpoint'
      });

      console.log('Encrypted endpoint response:', response);
      message.success('Encrypted endpoint test successful!');
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Encrypted endpoint test failed:', error);
      message.error(`Encrypted endpoint test failed: ${errorMessage}`);
    } finally {
      setLoading(prev => ({ ...prev, apiTest: false }));
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
      <Card>
        <Title level={3}>🔐 Encryption System Test</Title>
        
        <Paragraph>
          This component tests the encryption/decryption functionality in the UI.
          It verifies that data can be encrypted locally and decrypted properly.
        </Paragraph>

        {/* Encryption Status */}
        <Card size="small" style={{ marginBottom: '16px' }}>
          <Title level={5}>Frontend Encryption Status</Title>
          <Space>
            <Tag color={encryptionStatus.enabled ? 'green' : 'red'}>
              {encryptionStatus.enabled ? 'Enabled' : 'Disabled'}
            </Tag>
            <Tag color={encryptionStatus.hasKey ? 'green' : 'orange'}>
              {encryptionStatus.hasKey ? 'Key Configured' : 'Default Key'}
            </Tag>
          </Space>
        </Card>

        {/* Backend Configuration */}
        <Card size="small" style={{ marginBottom: '16px' }}>
          <Title level={5}>Backend Configuration</Title>
          {loading.backendConfig ? (
            <Text>Loading backend configuration...</Text>
          ) : backendConfig ? (
            <Space direction="vertical" size="small">
              <Space>
                <Tag color={backendConfig.enabled ? 'green' : 'red'}>
                  {backendConfig.enabled ? 'Encryption Enabled' : 'Encryption Disabled'}
                </Tag>
                <Tag color="blue">Algorithm: {backendConfig.algorithm}</Tag>
                <Tag color="purple">Transformation: {backendConfig.transformation}</Tag>
                <Tag color="orange">Key Length: {backendConfig.keyLength} bits</Tag>
              </Space>
              <Text type="secondary">
                This configuration is read from the backend and controls whether encryption is active.
              </Text>
            </Space>
          ) : (
            <Text type="danger">Failed to load backend configuration</Text>
          )}
        </Card>

        <Divider />

        {/* Test Buttons */}
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Title level={5}>Local Encryption Test</Title>
            <Text type="secondary">
              Tests encryption/decryption using the local encryption service
            </Text>
            <br />
            <Button 
              type="primary" 
              onClick={handleLocalTest}
              loading={loading.localTest}
              style={{ marginTop: '8px' }}
            >
              Run Local Test
            </Button>
          </div>

          <div>
            <Title level={5}>API Encryption Test</Title>
            <Text type="secondary">
              Tests encryption/decryption through the API service
            </Text>
            <br />
            <Button 
              type="primary" 
              onClick={handleApiTest}
              loading={loading.apiTest}
              style={{ marginTop: '8px' }}
            >
              Run API Test
            </Button>
          </div>

          <div>
            <Title level={5}>Encrypted Endpoint Test</Title>
            <Text type="secondary">
              Tests communication with encrypted backend endpoints
            </Text>
            <br />
            <Button 
              type="primary" 
              onClick={handleTestEncryptedEndpoint}
              loading={loading.apiTest}
              style={{ marginTop: '8px' }}
            >
              Test Encrypted Endpoint
            </Button>
          </div>
        </Space>

        <Divider />

        {/* Test Results */}
        <Title level={5}>Test Results</Title>
        
        {testResults.localTest && (
          <Card size="small" style={{ marginBottom: '8px' }}>
            <Space>
              <Tag color={testResults.localTest.success ? 'green' : 'red'}>
                Local Test
              </Tag>
              <Text>{testResults.localTest.message}</Text>
            </Space>
          </Card>
        )}

        {testResults.apiTest && (
          <Card size="small">
            <Space>
              <Tag color={testResults.apiTest.success ? 'green' : 'red'}>
                API Test
              </Tag>
              <Text>{testResults.apiTest.message}</Text>
            </Space>
          </Card>
        )}

        {/* Configuration Info */}
        <Divider />
        <Title level={5}>Configuration</Title>
        <Card size="small">
          <Space direction="vertical" size="small">
            <Text><strong>Encryption Enabled:</strong> {encryptionStatus.enabled ? 'Yes' : 'No'}</Text>
            <Text><strong>Secret Key:</strong> {encryptionStatus.hasKey ? 'Configured' : 'Using Default'}</Text>
            <Text><strong>Algorithm:</strong> AES-256-GCM</Text>
            <Text><strong>Environment:</strong> {process.env.NODE_ENV}</Text>
          </Space>
        </Card>
      </Card>
    </div>
  );
};

export default EncryptionTest;
